// Core packages
import { AbstractControl, FormArray } from "@angular/forms";

// Third party packages

// Custom packages

/**
 * Pure helpers of the paper document forms (add-documents page, property addNewDocuments page):
 * the radios next to "Numero di file" that give one document type to every file of the list at once.
 *
 * Those radios only drive the screen. Each file keeps its own `docType`, which is what the
 * back-end stores, so the key of the radios never reaches the payload.
 *
 * @since 3.0.0
 */

/** Model key of the "all files" radios: UI only, stripped from the payload */
export const ALL_FILES_DOC_TYPE_KEY = "allFilesDocType";

/**
 * True once "Numero di file" holds a number of files: the "all files" radios show up only then,
 * since before there is no list to give a type to. Empty and 0 leave the list without rows.
 *
 * @param model model of the paper document form
 */
export function hasFilesNumber(
  model: { [key: string]: any } | null | undefined
): boolean {
  return !!model && model.numFiles > 0;
}

/**
 * Gives `docType` to every file row of the list. Rows that already have it are left untouched,
 * so they emit no change. Nothing happens while no type is picked: the radios start empty and a
 * form reset sets them back to null.
 *
 * @param files `files` control of the form, a FormArray of `{ name, docType }` groups
 * @param docType type picked in the "all files" radios
 */
export function applyDocTypeToAllFiles(
  files: AbstractControl | null,
  docType: string | null
): void {
  if (!docType || !(files instanceof FormArray)) {
    return;
  }
  files.controls.forEach((file) => {
    const docTypeControl = file.get("docType");
    if (docTypeControl && docTypeControl.value !== docType) {
      docTypeControl.setValue(docType);
    }
  });
}

/**
 * Gives the picked type to the files just added to the list. Raising "Numero di file" grows the
 * `files` array of the model and leaves an empty slot for each new row: a row whose slot is filled
 * here is born with the picked type instead of the radio default ("report"). Existing rows are
 * left untouched.
 *
 * @param files `files` array of the model, already resized to the new number of files
 * @param docType type picked in the "all files" radios
 */
export function fillNewFilesDocType(
  files: any[] | null | undefined,
  docType: string | null
): void {
  if (!docType || !Array.isArray(files)) {
    return;
  }
  for (let i = 0; i < files.length; i++) {
    if (!files[i]) {
      files[i] = { docType };
    }
  }
}

/**
 * Clears the "all files" radios when one file gets another type: they no longer describe the whole
 * list, and once cleared the user can pick the same type again to realign every file.
 *
 * @param allFilesDocType control of the "all files" radios
 * @param fileDocType new type of one file
 */
export function clearAllFilesDocTypeOnMismatch(
  allFilesDocType: AbstractControl | null,
  fileDocType: string | null
): void {
  if (
    allFilesDocType &&
    allFilesDocType.value &&
    fileDocType !== allFilesDocType.value
  ) {
    allFilesDocType.setValue(null);
  }
}

/**
 * Shallow copy of the form model without the key of the "all files" radios, ready for the payload
 *
 * @param model model of the paper document form
 */
export function withoutAllFilesDocType(model: {
  [key: string]: any;
}): { [key: string]: any } {
  const data = { ...model };
  delete data[ALL_FILES_DOC_TYPE_KEY];
  return data;
}
